import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  ArrowLeft, Search, Filter, RefreshCw, Download, Clock,
  User, Users, Calendar, FileText, Settings, Shield, LogIn,
  ChevronLeft, ChevronRight, Loader2, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const categoryIcons = {
  auth: LogIn,
  user: User,
  horse: Users,
  course: Calendar,
  billing: FileText,
  group: Users,
  settings: Settings,
  admin: Shield,
};

// Category colors
const categoryColors = {
  auth: 'bg-blue-100 text-blue-700',
  user: 'bg-green-100 text-green-700',
  horse: 'bg-amber-100 text-amber-700',
  course: 'bg-purple-100 text-purple-700',
  billing: 'bg-rose-100 text-rose-700',
  group: 'bg-cyan-100 text-cyan-700',
  settings: 'bg-stone-100 text-stone-700',
  admin: 'bg-red-100 text-red-700',
};

export function SuperAdminActivityLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [actions, setActions] = useState({});
  
  // Filters
  const [filters, setFilters] = useState({
    club_id: '',
    user_email: '',
    category: '',
    days: '7',
  });
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchCategories();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, filters]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/superadmin/activity-logs/categories`);
      setCategories(res.data.categories);
      setActions(res.data.actions);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString(),
        days: filters.days,
      });
      
      if (filters.club_id) params.append('club_id', filters.club_id);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.category) params.append('category', filters.category);

      const res = await axios.get(`${API_URL}/api/superadmin/activity-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleExport = () => {
    // Export as CSV
    const headers = ['Date', 'Utilisateur', 'Club', 'Catégorie', 'Action', 'Détails'];
    const rows = logs.map(log => [
      format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm', { locale: fr }),
      log.user_email,
      log.club_name || '-',
      log.category_label,
      log.action_label,
      log.target_name || log.details ? JSON.stringify(log.details) : '-'
    ]);
    
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `activity_logs_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
    toast.success('Export téléchargé');
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/superadmin')}
                className="text-stone-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-stone-800">Journal d'activité</h1>
                <p className="text-sm text-stone-500">Suivi des actions utilisateurs</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchLogs}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={logs.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter CSV
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtres
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Période</label>
                <Select value={filters.days} onValueChange={(v) => handleFilterChange('days', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Dernières 24h</SelectItem>
                    <SelectItem value="7">7 derniers jours</SelectItem>
                    <SelectItem value="30">30 derniers jours</SelectItem>
                    <SelectItem value="90">3 derniers mois</SelectItem>
                    <SelectItem value="180">6 derniers mois</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Catégorie</label>
                <Select value={filters.category || "all"} onValueChange={(v) => handleFilterChange('category', v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    {Object.entries(categories).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Utilisateur</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    placeholder="Email..."
                    value={filters.user_email}
                    onChange={(e) => handleFilterChange('user_email', e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="flex items-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setFilters({ club_id: '', user_email: '', category: '', days: '7' });
                    setPage(0);
                  }}
                  className="text-stone-600"
                >
                  Réinitialiser
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-stone-600">
            {total} action{total > 1 ? 's' : ''} trouvée{total > 1 ? 's' : ''}
          </p>
        </div>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-stone-500">
                <AlertTriangle className="w-12 h-12 mb-4 text-stone-300" />
                <p>Aucune activité trouvée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Utilisateur</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Club</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Détails</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {logs.map((log, idx) => {
                      const IconComponent = categoryIcons[log.category] || Clock;
                      return (
                        <tr key={idx} className="hover:bg-stone-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-4 h-4 text-stone-400" />
                              <span className="text-stone-600">
                                {format(new Date(log.timestamp), 'dd/MM/yyyy', { locale: fr })}
                              </span>
                              <span className="text-stone-400">
                                {format(new Date(log.timestamp), 'HH:mm', { locale: fr })}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-stone-800">{log.user_email}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-stone-600">{log.club_name || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Badge className={`${categoryColors[log.category] || 'bg-stone-100'} flex items-center gap-1`}>
                                <IconComponent className="w-3 h-3" />
                                {log.category_label}
                              </Badge>
                              <span className="text-sm text-stone-700">{log.action_label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {log.target_name && (
                              <span className="text-sm text-stone-600">{log.target_name}</span>
                            )}
                            {log.details && !log.target_name && (
                              <span className="text-xs text-stone-400">
                                {JSON.stringify(log.details).substring(0, 50)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-stone-600">
              Page {page + 1} sur {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Info RGPD */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Conservation des données :</strong> Les logs sont conservés pendant 6 mois maximum, 
            conformément aux recommandations RGPD. Les données plus anciennes sont automatiquement supprimées.
          </p>
        </div>
      </main>
    </div>
  );
}

export default SuperAdminActivityLogs;
